import React, { useEffect, useLayoutEffect, useState } from 'react';
import { updateUserProfile } from '../services/firebase';
import { APP } from '../config/app';

/**
 * Coachmarks over real UI (data-tour="…"), not a blocking text modal.
 * Steps that have no matching target are skipped.
 */
const STEPS = [
  { key: 'pickup', selector: '[data-tour="pickup"]' },
  { key: 'where', selector: '[data-tour="where"]' },
  { key: 'shortcuts', selector: '[data-tour="shortcuts"]' },
  { key: 'book', selector: '[data-tour="book"]' },
];

const PAD = 8;

export default function FirstTimeTour({ t, uid, onDone }) {
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const [rect, setRect] = useState(null);
  const [steps, setSteps] = useState(STEPS);

  useEffect(() => {
    const available = STEPS.filter((s) => document.querySelector(s.selector));
    setSteps(available.length ? available : STEPS.slice(0, 1));
    setI(0);
  }, []);

  const step = steps[i] || steps[0];

  useLayoutEffect(() => {
    if (!step) return undefined;
    const el = document.querySelector(step.selector);
    if (!el) {
      setRect(null);
      return undefined;
    }
    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({
        top: Math.max(8, r.top - PAD),
        left: Math.max(8, r.left - PAD),
        width: Math.min(window.innerWidth - 16, r.width + PAD * 2),
        height: r.height + PAD * 2,
      });
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step, i]);

  const finish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (uid) await updateUserProfile(uid, { tourDone: true });
    } catch (e) {
      console.warn('tourDone save failed:', e?.message || e);
    }
    onDone?.();
  };

  const next = () => {
    if (i >= steps.length - 1) {
      finish();
      return;
    }
    setI((n) => n + 1);
  };

  if (!step) return null;

  const tipTop = rect
    ? (rect.top > window.innerHeight / 2
        ? Math.max(16, rect.top - 180)
        : Math.min(window.innerHeight - 200, rect.top + rect.height + 12))
    : null;

  const tipStyle = rect
    ? {
        top: tipTop,
        left: 16,
        right: 16,
      }
    : { bottom: 24, left: 16, right: 16 };

  return (
    <div className="coach-root" role="dialog" aria-modal="true" aria-labelledby="coach-title">
      <div className="coach-catch" onClick={next} role="presentation" />
      {rect && (
        <div
          className="coach-hole"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        />
      )}
      <div className="coach-tip" style={tipStyle}>
        <div className="coach-step">{i + 1}/{steps.length}</div>
        <h3 id="coach-title">{t(`tour.${step.key}.title`)}</h3>
        <p>{t(`tour.${step.key}.body`, { city: APP.city })}</p>
        <div className="coach-actions">
          <button type="button" className="btn btn-ghost" disabled={busy} onClick={finish}>
            {t('tour.skip')}
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={next}>
            {i >= steps.length - 1 ? t('tour.done') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
